<template>
  <div class="tracked-keys">
    <!-- Le calcul lit la dernière configuration de chaque borne : quelques secondes la première fois, on le montre -->
    <div v-if="!data" class="text-center my-6">
      <v-progress-circular indeterminate color="primary" />
      <div class="text-caption text--secondary mt-2">{{ $t('charging-point-tracked-keys.loading') }}</div>
    </div>
    <template v-else>
      <v-progress-linear :active="loading" indeterminate color="primary" height="3" class="loading-bar" />
      <!-- Ouvert depuis la liste du parc : on y revient, et la fiche du constructeur est à un clic -->
      <div v-if="focus && !manufacturerId" class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
        <v-btn small text @click="unfocus"><v-icon small left>mdi-arrow-left</v-icon>{{ $t('charging-point-tracked-keys.all-manufacturers') }}</v-btn>
        <span class="text-h6">{{ focusName }}</span>
        <v-spacer />
        <v-btn small text color="primary" :to="{name: 'manufacturer.configuration', params: {id: focus}}">{{ $t('charging-point-tracked-keys.open-sheet') }}</v-btn>
      </div>

      <!-- Où en est la lecture du périmètre ; rien de lu : on dit pourquoi tout est vide -->
      <v-alert v-if="!data.coverage.read" type="info" text dense>
        <div class="font-weight-medium">{{ $t('charging-point-tracked-keys.nothing-read') }}</div>
        <div>{{ $t('charging-point-tracked-keys.nothing-read-hint', {total: data.coverage.chargingPoints}) }}</div>
      </v-alert>
      <div v-else class="text-caption text--secondary mb-2">
        {{ $t('charging-point-tracked-keys.coverage', {read: data.coverage.read, total: data.coverage.chargingPoints, never: data.coverage.chargingPoints - data.coverage.read, waiting: data.coverage.waiting}) }}
      </div>
      <v-card outlined class="mb-6">
        <v-card-text class="d-flex align-center flex-wrap" style="gap: 16px">
          <div>
            <div class="text-h6">{{ data.assessed ? Math.round((data.assessed - data.deviating) * 100 / data.assessed) + ' %' : '-' }}</div>
            <div class="text-caption text--secondary">{{ $t('charging-point-tracked-keys.conform', {conform: data.assessed - data.deviating, assessed: data.assessed}) }}</div>
          </div>
          <v-spacer />
          <span class="text-caption text--secondary" style="max-width: 460px">{{ focus ? $t('charging-point-tracked-keys.manufacturer-hint') : $t('charging-point-tracked-keys.hint') }}</span>
          <v-text-field v-model="search" :label="$t('charging-point-tracked-keys.search')" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable style="max-width: 240px" />
        </v-card-text>
      </v-card>

      <!-- Le parc : les clés OCPP standard, nos clés communes, puis chaque constructeur et ses clés propres -->
      <template v-if="!focus">
        <div class="section-title">
          <span>{{ $t('charging-point-tracked-keys.standard-title') }}</span>
          <v-chip x-small outlined class="ml-2">OCPP 1.6</v-chip>
        </div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.standard-hint') }}</div>
        <tracked-key-table :keys="standardKeys" :height="standardKeys.length > 12 ? 420 : undefined" :empty="$t('charging-point-tracked-keys.none-found')" @open="open">
          <template #actions="{item}">
            <v-btn icon x-small :aria-label="$t('edit')" @click="editKey(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
          </template>
        </tracked-key-table>

        <div class="section-title mt-8">
          <span>{{ $t('charging-point-tracked-keys.common-title') }}</span>
          <v-spacer />
          <v-btn small color="primary" @click="newKey(null)"><v-icon small left>mdi-plus</v-icon>{{ $t('charging-point-tracked-keys.add-common') }}</v-btn>
        </div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.common-hint') }}</div>
        <tracked-key-table :keys="commonKeys" :empty="$t('charging-point-tracked-keys.common-none')" where :targets="data.targets" @open="open">
          <template #actions="{item}">
            <v-btn icon x-small :aria-label="$t('edit')" @click="editKey(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
            <v-btn icon x-small :aria-label="$t('delete')" @click="removeKey(item)"><v-icon x-small>mdi-delete</v-icon></v-btn>
          </template>
        </tracked-key-table>

        <div class="section-title mt-8">
          <span>{{ $t('charging-point-tracked-keys.informations-title') }}</span>
          <v-spacer />
          <v-btn small color="primary" @click="newKey(null, true)"><v-icon small left>mdi-plus</v-icon>{{ $t('charging-point-tracked-keys.add-information') }}</v-btn>
        </div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.informations-hint') }}</div>
        <tracked-key-table :keys="informationKeys" :empty="$t('charging-point-tracked-keys.informations-none')" informations where :targets="data.targets" @open="open">
          <template #actions="{item}">
            <v-btn icon x-small :aria-label="$t('edit')" @click="editKey(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
            <v-btn icon x-small :aria-label="$t('delete')" @click="removeKey(item)"><v-icon x-small>mdi-delete</v-icon></v-btn>
          </template>
        </tracked-key-table>

        <div class="section-title mt-8">{{ $t('charging-point-tracked-keys.by-manufacturer') }}</div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.by-manufacturer-hint') }}</div>
        <v-row dense>
          <v-col v-for="manufacturer in shownManufacturers" :key="manufacturer.id" cols="12" sm="6" md="4" lg="3">
            <v-card outlined class="manufacturer-card" @click="focusOn(manufacturer)">
              <v-card-text>
                <div class="d-flex align-center">
                  <span class="text-subtitle-1 font-weight-medium text-truncate">{{ manufacturer.name }}</span>
                  <v-spacer />
                  <v-icon small>mdi-chevron-right</v-icon>
                </div>
                <div class="text-caption text--secondary mb-2">{{ $tc('charging-point-tracked-keys.charging-points', manufacturer.chargingPoints, {count: manufacturer.chargingPoints}) }}</div>
                <div class="d-flex flex-wrap" style="gap: 4px">
                  <v-chip x-small :color="manufacturer.ownKeys ? 'primary' : undefined" :outlined="!manufacturer.ownKeys" :text-color="manufacturer.ownKeys ? 'white' : undefined">
                    {{ $tc('charging-point-tracked-keys.own-count', manufacturer.ownKeys, {count: manufacturer.ownKeys}) }}
                  </v-chip>
                  <v-chip v-if="manufacturer.informations" x-small outlined>{{ $tc('charging-point-tracked-keys.information-count', manufacturer.informations, {count: manufacturer.informations}) }}</v-chip>
                  <v-chip v-if="manufacturer.commonKeys" x-small outlined>{{ $tc('charging-point-tracked-keys.common-count', manufacturer.commonKeys, {count: manufacturer.commonKeys}) }}</v-chip>
                  <v-chip v-if="manufacturer.deviating" x-small color="orange" text-color="white">{{ $tc('charging-point-tracked-keys.deviating-count', manufacturer.deviating, {count: manufacturer.deviating}) }}</v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="!shownManufacturers.length" cols="12" class="text--secondary text-body-2">{{ search ? $t('charging-point-tracked-keys.none-found') : $t('charging-point-tracked-keys.manufacturers-none') }}</v-col>
        </v-row>
      </template>

      <!-- Un constructeur : ses clés propres, nos clés communes déclinées chez lui, et ce que ses bornes annoncent d'autre -->
      <template v-else>
        <div class="section-title">
          <span>{{ $t('charging-point-tracked-keys.own-title', {name: focusName}) }}</span>
          <v-spacer />
          <v-btn small color="primary" @click="newKey(focus)"><v-icon small left>mdi-plus</v-icon>{{ $t('charging-point-tracked-keys.add-own') }}</v-btn>
        </div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.own-hint') }}</div>
        <tracked-key-table :keys="ownKeysTracked" :empty="$t('charging-point-tracked-keys.own-none')" :manufacturer-id="focus" @open="open" @remove-variant="removeVariant">
          <template #actions="{item}">
            <v-btn x-small text color="primary" @click="editVariant(item)">{{ $t('charging-point-tracked-keys.decline-own') }}</v-btn>
            <v-btn icon x-small :aria-label="$t('edit')" @click="editKey(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
            <v-btn icon x-small :aria-label="$t('delete')" @click="removeKey(item)"><v-icon x-small>mdi-delete</v-icon></v-btn>
          </template>
        </tracked-key-table>

        <div class="section-title mt-8">{{ $t('charging-point-tracked-keys.common-here-title', {name: focusName}) }}</div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.common-here-hint') }}</div>
        <tracked-key-table :keys="commonKeys" :empty="$t('charging-point-tracked-keys.common-none')" :manufacturer-id="focus" @open="open" @remove-variant="removeVariant">
          <template #actions="{item}">
            <v-btn x-small text color="primary" @click="editVariant(item)">{{ $t('charging-point-tracked-keys.decline') }}</v-btn>
          </template>
        </tracked-key-table>

        <div class="section-title mt-8">
          <span>{{ $t('charging-point-tracked-keys.informations-here-title', {name: focusName}) }}</span>
          <v-spacer />
          <v-btn small color="primary" @click="newKey(focus, true)"><v-icon small left>mdi-plus</v-icon>{{ $t('charging-point-tracked-keys.add-own-information') }}</v-btn>
        </div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.informations-here-hint') }}</div>
        <tracked-key-table :keys="informationKeys" :empty="$t('charging-point-tracked-keys.informations-none')" informations :manufacturer-id="focus" @open="open" @remove-variant="removeVariant">
          <template #actions="{item}">
            <v-btn x-small text color="primary" @click="editVariant(item)">{{ item.kind === 'manufacturer' ? $t('charging-point-tracked-keys.decline-own') : $t('charging-point-tracked-keys.decline') }}</v-btn>
            <v-btn v-if="item.kind === 'manufacturer'" icon x-small :aria-label="$t('edit')" @click="editKey(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
            <v-btn v-if="item.kind === 'manufacturer'" icon x-small :aria-label="$t('delete')" @click="removeKey(item)"><v-icon x-small>mdi-delete</v-icon></v-btn>
          </template>
        </tracked-key-table>

        <div class="section-title mt-8">
          <span>{{ $t('charging-point-tracked-keys.raw-title') }}</span>
          <v-spacer />
          <v-text-field v-model="rawFilter" :placeholder="$t('charging-point-tracked-keys.filter')" prepend-inner-icon="mdi-filter-variant" dense hide-details clearable class="mt-0 pt-0 mr-3" style="max-width: 260px" />
          <!-- Les plus répandues d'abord, ou par nom -->
          <v-btn-toggle v-model="rawSort" mandatory dense color="primary">
            <v-btn small value="count"><v-icon small left>mdi-sort-numeric-descending</v-icon>{{ $t('charging-point-tracked-keys.sort-count') }}</v-btn>
            <v-btn small value="name"><v-icon small left>mdi-sort-alphabetical-ascending</v-icon>{{ $t('charging-point-tracked-keys.sort-name') }}</v-btn>
          </v-btn-toggle>
        </div>
        <div class="text-caption text--secondary mb-2">{{ $t('charging-point-tracked-keys.raw-hint') }}</div>
        <v-card outlined>
          <v-simple-table dense>
            <tbody>
              <tr v-if="rawKeys === null"><td class="text-center"><v-progress-circular indeterminate size="20" color="primary" /></td></tr>
              <tr v-else-if="!untrackedKeys.length"><td class="text--secondary">{{ $t('charging-point-tracked-keys.own-keys-none') }}</td></tr>
              <tr v-for="key in untrackedKeys" :key="key.key" class="clickable" @click="rawDetail = {open: true, key: key.key}">
                <td class="mono">{{ key.key }}</td>
                <td class="text-right text-caption">{{ $tc('charging-point-tracked-keys.charging-points', key.chargingPoints, {count: key.chargingPoints}) }}</td>
                <td class="text-right text-no-wrap" style="width: 1%" @click.stop>
                  <v-btn x-small text color="primary" @click="follow(key)">{{ $t('charging-point-tracked-keys.follow') }}</v-btn>
                  <v-btn x-small text @click="follow(key, true)">{{ $t('charging-point-tracked-keys.follow-information') }}</v-btn>
                  <v-btn v-if="data.keys.some(tracked => tracked.kind === 'common')" x-small text @click="attach(key)">{{ $t('charging-point-tracked-keys.attach') }}</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <!-- Les clés standard aussi, mais chez lui : leurs chiffres sur ses bornes seules ; elles se règlent pour tout le parc -->
        <v-expansion-panels flat class="mt-8">
          <v-expansion-panel class="standard-here">
            <v-expansion-panel-header class="px-0">
              <div>
                <div class="section-title">
                  <span>{{ $t('charging-point-tracked-keys.standard-here-title', {name: focusName}) }}</span>
                  <v-chip x-small outlined class="ml-2">OCPP 1.6</v-chip>
                </div>
                <div class="text-caption text--secondary">{{ $t('charging-point-tracked-keys.standard-here-hint') }}</div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <tracked-key-table :keys="standardKeys" :height="standardKeys.length > 12 ? 420 : undefined" :empty="$t('charging-point-tracked-keys.none-found')" @open="open" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </template>

    <!-- Une clé annoncée, pas encore suivie : ses valeurs et ses bornes, pour décider quoi en faire -->
    <raw-key-detail v-model="rawDetail.open" :key-name="rawDetail.key" :filters="scope">
      <template #actions>
        <v-btn small text color="primary" @click="rawDetail.open = false; follow({key: rawDetail.key})">{{ $t('charging-point-tracked-keys.follow') }}</v-btn>
        <v-btn small text @click="rawDetail.open = false; follow({key: rawDetail.key}, true)">{{ $t('charging-point-tracked-keys.follow-information') }}</v-btn>
        <v-btn v-if="commonCodes.length" small text @click="rawDetail.open = false; attach({key: rawDetail.key})">{{ $t('charging-point-tracked-keys.attach') }}</v-btn>
      </template>
    </raw-key-detail>

    <tracked-key-detail v-model="detail.open" :code="detail.code" :filters="scope" @batch-created="batchCreated" />

    <!-- Créer une clé à nous, ou régler une clé suivie (tout le parc) -->
    <v-dialog v-model="keyForm.open" max-width="520">
      <v-card v-if="keyForm.open">
        <v-card-title>{{ keyForm.existing ? $t('charging-point-tracked-keys.edit', {code: keyForm.code}) : keyFormTitle }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="keyForm.code" :label="keyForm.manufacturerId ? $t('charging-point-tracked-keys.code-own') : $t('charging-point-tracked-keys.code')" :hint="keyForm.manufacturerId ? $t('charging-point-tracked-keys.code-own-hint') : $t('charging-point-tracked-keys.code-hint')" persistent-hint :disabled="keyForm.existing" outlined dense class="mono mb-2" />
          <v-text-field v-model="keyForm.label" :label="$t('charging-point-tracked-keys.label')" outlined dense />
          <v-textarea v-model="keyForm.description" :label="$t('charging-point-tracked-keys.description')" rows="2" auto-grow outlined dense />
          <v-text-field v-if="keyForm.information" v-model="keyForm.unit" :label="$t('charging-point-tracked-keys.unit')" :hint="$t('charging-point-tracked-keys.unit-hint')" persistent-hint outlined dense />
          <v-text-field v-else v-model="keyForm.expected" :label="keyForm.manufacturerId ? $t('charging-point-tracked-keys.expected-own', {name: focusName}) : $t('charging-point-tracked-keys.expected-fleet')" :hint="$t('charging-point-tracked-keys.expected-hint')" persistent-hint outlined dense class="mono" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="keyForm.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!keyForm.code" :loading="saving" @click="saveKey">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Chez ce constructeur : le nom de la clé et/ou la valeur attendue, pour lui, une gamme ou un type, des versions -->
    <v-dialog v-model="variantForm.open" max-width="560">
      <v-card v-if="variantForm.open">
        <v-card-title>{{ $t('charging-point-tracked-keys.decline-title', {code: variantForm.code}) }}</v-card-title>
        <v-card-text>
          <v-select v-model="variantForm.layer" :items="layerItems" :label="$t('charging-point-tracked-keys.level')" outlined dense @change="variantForm.targetId = variantForm.layer === 'manufacturer' ? focus : null" />
          <v-autocomplete v-if="variantForm.layer !== 'manufacturer'" v-model="variantForm.targetId" :items="targetItems" item-text="name" item-value="id" :label="$t('charging-point-tracked-keys.target')" outlined dense />
          <v-text-field v-model="variantForm.versionPattern" :label="$t('charging-point-tracked-keys.versions')" :hint="$t('charging-point-tracked-keys.versions-hint')" persistent-hint outlined dense class="mono mb-2" />
          <!-- Un ou plusieurs noms : Entrée fait une puce ; on propose ce que ses bornes annoncent -->
          <v-combobox
              v-model="variantForm.keys"
              :items="rawKeyNames"
              :label="$t('charging-point-tracked-keys.key-here')"
              :hint="$t('charging-point-tracked-keys.key-here-hint', {code: variantForm.code})"
              persistent-hint
              multiple
              small-chips
              deletable-chips
              hide-selected
              outlined
              dense
              class="mono mb-2"
          />
          <v-text-field v-if="!variantForm.information" v-model="variantForm.expected" :label="$t('charging-point-tracked-keys.expected-here')" outlined dense class="mono" />
          <v-text-field v-model="variantForm.note" :label="$t('charging-point-tracked-keys.note')" outlined dense hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="variantForm.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!variantForm.targetId || (!variantForm.keys.length && !variantForm.expected)" :loading="saving" @click="saveVariant">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Une clé que ses bornes annoncent : c'est le vrai nom, chez lui, d'une de nos clés communes -->
    <v-dialog v-model="attachForm.open" max-width="480">
      <v-card v-if="attachForm.open">
        <v-card-title>{{ $t('charging-point-tracked-keys.attach-title', {key: attachForm.key}) }}</v-card-title>
        <v-card-text>
          <v-select v-model="attachForm.code" :items="commonCodes" :label="$t('charging-point-tracked-keys.attach-to')" outlined dense hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="attachForm.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!attachForm.code" :loading="saving" @click="saveAttach">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import TrackedKeyDetail from "./TrackedKeyDetail.vue";
import TrackedKeyTable from "./TrackedKeyTable.vue";
import RawKeyDetail from "./RawKeyDetail.vue";

/**
 * Les clés suivies. Sur le parc : les clés OCPP standard (communes à tous les constructeurs), nos clés communes (l'adresse
 * du backend, l'APN…, chacune sous son vrai nom chez chaque constructeur), puis les constructeurs, chacun avec ses clés
 * propres : la même chaîne chez deux constructeurs n'est pas la même clé (ni la même unité). Un constructeur s'ouvre ici
 * (`?keysManufacturer=`) ou dans sa fiche (`manufacturerId`) : ses clés propres, nos clés communes déclinées chez lui,
 * et ce que ses bornes annoncent d'autre, qu'on suit d'un clic. Un clic sur une clé ouvre sa répartition, d'où on
 * applique une valeur en masse.
 */
export default {
  components: {TrackedKeyDetail, TrackedKeyTable, RawKeyDetail},

  props: {
    filters: {type: Object, default: () => ({})},
    manufacturerId: {type: String, default: null},
  },

  data() {
    return {
      data: null,
      search: '',
      saving: false,
      detail: {open: false, code: null},
      rawDetail: {open: false, key: null},
      rawSort: 'count',
      loading: false,
      request: 0,
      rawFilter: '',
      rawKeys: null,
      keyForm: {open: false},
      variantForm: {open: false},
      attachForm: {open: false},
    }
  },

  computed: {
    // Le constructeur regardé : celui de la fiche, celui ouvert depuis le parc, ou celui du filtre.
    focus() {
      return this.manufacturerId || this.$route.query.keysManufacturer || this.filters.manufacturerId || null
    },
    focusName() {
      const target = (this.data?.targets || []).find(target => target.layer === 'manufacturer' && target.id === this.focus)
      return target ? target.name : ''
    },
    scope() {
      return this.focus ? {...this.filters, manufacturerId: this.focus} : this.filters
    },
    searched() {
      const search = (this.search || '').toLowerCase()
      return this.data.keys.filter(key => !search || key.code.toLowerCase().includes(search) || (key.label || '').toLowerCase().includes(search))
    },
    standardKeys() {
      return this.searched.filter(key => key.kind === 'standard')
    },
    commonKeys() {
      return this.searched.filter(key => key.kind === 'common' && !key.information)
    },
    ownKeysTracked() {
      return this.searched.filter(key => key.kind === 'manufacturer' && !key.information && key.manufacturerId === this.focus)
    },
    // Les informations (lues, jamais réglées) : communes sur le parc ; chez un constructeur, les siennes en plus.
    informationKeys() {
      return this.searched.filter(key => key.information && (key.manufacturerId === null || key.manufacturerId === this.focus))
    },
    keyFormTitle() {
      if (this.keyForm.information) {
        return this.keyForm.manufacturerId ? this.$t('charging-point-tracked-keys.add-own-information-title', {name: this.focusName}) : this.$t('charging-point-tracked-keys.add-information')
      }
      return this.keyForm.manufacturerId ? this.$t('charging-point-tracked-keys.add-own-title', {name: this.focusName}) : this.$t('charging-point-tracked-keys.add-common')
    },
    // Les clés que ses bornes annoncent : proposées comme noms possibles.
    rawKeyNames() {
      return (this.rawKeys || []).map(key => key.key).sort((a, b) => a.localeCompare(b))
    },
    commonCodes() {
      return this.data.keys.filter(key => key.kind === 'common').map(key => key.code)
    },
    shownManufacturers() {
      const search = (this.search || '').toLowerCase()
      return (this.data.manufacturers || []).filter(manufacturer => !search || manufacturer.name.toLowerCase().includes(search))
    },
    // Ce que ses bornes annoncent, ni standard ni déjà suivi (sous notre nom ou sous le leur).
    untrackedKeys() {
      if (!this.rawKeys || !this.data) {
        return []
      }
      const known = new Set()
      this.data.keys.forEach(key => {
        known.add(key.code.toLowerCase())
        // Un réglage peut lister plusieurs noms (« evb_ServerURL, OCPP_SERVER_URL »).
        this.variantsHere(key).forEach(variant => (variant.key || '').split(',').map(name => name.trim()).filter(name => name).forEach(name => known.add(name.toLowerCase())))
      })
      const search = (this.search || '').toLowerCase()
      return this.rawKeys
        .filter(key => !known.has(key.key.toLowerCase()) && (!search || key.key.toLowerCase().includes(search)))
        .filter(key => !this.rawFilter || key.key.toLowerCase().includes(this.rawFilter.trim().toLowerCase()))
        .slice()
        .sort((a, b) => this.rawSort === 'name'
          ? a.key.localeCompare(b.key, undefined, {sensitivity: 'base'})
          : b.chargingPoints - a.chargingPoints || a.key.localeCompare(b.key, undefined, {sensitivity: 'base'}))
    },
    // La clé d'un constructeur se décline sur ses gammes et ses types ; une clé commune, aussi chez lui.
    layerItems() {
      const layers = this.variantForm.kind === 'manufacturer' ? ['product-line', 'charging-point-type'] : ['manufacturer', 'product-line', 'charging-point-type']
      return layers.map(layer => ({value: layer, text: this.$t('charging-point-tracked-keys.layers.' + layer)}))
    },
    targetItems() {
      return this.data.targets.filter(target => target.layer === this.variantForm.layer && target.manufacturerId === this.focus).sort((a, b) => a.name.localeCompare(b.name))
    },
  },

  watch: {
    scope: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      this.rawKeys = null
      if (this.focus) {
        ChargingPointConfigurationRepository.search({manufacturerId: this.focus}).then(result => {
          this.rawKeys = result.keys
        }).catch(err => {
          this.notifyError(err)
        })
      }
      // Deux chargements croisés (un filtre changé pendant le calcul) : seule la réponse du dernier s'affiche.
      const request = ++this.request
      this.loading = true
      return ChargingPointConfigurationRepository.trackedKeys(this.scope).then(data => {
        if (request === this.request) {
          this.data = data
        }
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        if (request === this.request) {
          this.loading = false
        }
      })
    },

    focusOn(manufacturer) {
      this.search = ''
      this.$router.push({query: {...this.$route.query, keysManufacturer: manufacturer.id}})
    },

    unfocus() {
      const query = {...this.$route.query}
      delete query.keysManufacturer
      this.$router.push({query})
    },

    variantsHere(key) {
      return key.variants.filter(variant => variant.manufacturerId === this.focus)
    },

    open(key) {
      this.detail = {open: true, code: key.code}
    },

    newKey(manufacturerId, information = false) {
      this.keyForm = {open: true, existing: false, manufacturerId, information, unit: '', code: '', label: '', description: '', expected: ''}
    },

    // Suivre ce que ses bornes annoncent : une clé propre à ce constructeur, sous ce nom.
    follow(raw, information = false) {
      this.keyForm = {open: true, existing: false, manufacturerId: this.focus, information, unit: '', code: raw.key, label: '', description: '', expected: ''}
    },

    // Ou le rattacher à une clé commune : c'est son vrai nom chez lui.
    attach(raw) {
      this.attachForm = {open: true, key: raw.key, code: null}
    },

    saveAttach() {
      this.saving = true
      ChargingPointConfigurationRepository.setVariant(this.attachForm.code, {layer: 'manufacturer', targetId: this.focus, key: this.attachForm.key}).then(() => {
        this.attachForm.open = false
        return this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.saving = false
      })
    },

    editKey(key) {
      this.keyForm = {open: true, existing: true, manufacturerId: key.manufacturerId, information: key.information, unit: key.unit || '', code: key.code, label: key.label || '', description: key.description || '', expected: key.expected ?? ''}
    },

    saveKey() {
      this.saving = true
      const {code, label, description, expected, manufacturerId, information, unit} = this.keyForm
      ChargingPointConfigurationRepository.saveTrackedKey({code: code.trim(), manufacturerId, information, unit: unit || null, label, description, expected: information || expected === '' ? null : expected}).then(() => {
        this.keyForm.open = false
        return this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.saving = false
      })
    },

    removeKey(key) {
      this.$dialog.confirm({text: this.$t('charging-point-tracked-keys.remove-confirm', {code: key.code})}).then(result => {
        if (result) {
          ChargingPointConfigurationRepository.deleteTrackedKey(key.code, key.manufacturerId).then(() => this.load()).catch(err => {
            this.notifyError(err)
          })
        }
      })
    },

    editVariant(key) {
      const layer = key.kind === 'manufacturer' ? 'product-line' : 'manufacturer'
      this.variantForm = {open: true, code: key.code, kind: key.kind, information: key.information, manufacturerId: key.manufacturerId, layer, targetId: layer === 'manufacturer' ? this.focus : null, versionPattern: '', keys: [], expected: '', note: ''}
    },

    saveVariant() {
      this.saving = true
      const {code, manufacturerId, layer, targetId, versionPattern, keys, expected, note} = this.variantForm
      // Les noms possibles, dans l'ordre : chaque borne prend le premier qu'elle porte.
      const key = keys.map(name => String(name).trim()).filter(name => name).join(', ') || null
      ChargingPointConfigurationRepository.setVariant(code, {manufacturerId, layer, targetId, versionPattern, key, expected: expected === '' ? null : expected, note}).then(() => {
        this.variantForm.open = false
        return this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.saving = false
      })
    },

    removeVariant(key, variant) {
      this.$dialog.confirm({text: this.$t('charging-point-tracked-keys.remove-variant-confirm', {code: key.code, target: variant.targetName})}).then(result => {
        if (result) {
          ChargingPointConfigurationRepository.removeVariant(key.code, variant.id, key.manufacturerId).then(() => this.load()).catch(err => {
            this.notifyError(err)
          })
        }
      })
    },

    // Un lot lancé : on va le suivre (sur la page du parc, onglet des modifications en masse).
    batchCreated(batchId) {
      this.detail.open = false
      this.$emit('batch-created', batchId)
      if (this.manufacturerId) {
        this.$router.push({name: 'charging-points.configuration', query: {tab: 'batches', batch: batchId}})
      }
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.clickable {
  cursor: pointer;
}
.loading-bar {
  position: sticky;
  top: 0;
  z-index: 2;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 2px;
}
.manufacturer-card {
  height: 100%;
}
.standard-here ::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>
